import React from "react";

export type AuthUser = {
  id: string;
  tenantId?: string;
  email: string;
  role?: string | null;
  isPlatformAdmin?: boolean;
};

export type TenantSummary = {
  id: string;
  name: string;
  slug?: string;
};

type AuthContextValue = {
  user: AuthUser | null;
  activeTenantId: string | null;
  tenants: TenantSummary[];
  loading: boolean;
  refresh: () => Promise<boolean>;
  setActiveTenant: (tenantId: string) => Promise<void>;
  setUser: (user: AuthUser | null) => void;
};

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);
const ACTIVE_TENANT_STORAGE_KEY = "pericles_active_tenant";

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [activeTenantId, setActiveTenantId] = React.useState<string | null>(null);
  const [tenants, setTenants] = React.useState<TenantSummary[]>([]);
  const authSnapshotRef = React.useRef<{
    user: AuthUser | null;
    activeTenantId: string | null;
    tenants: TenantSummary[];
  }>({
    user: null,
    activeTenantId: null,
    tenants: [],
  });

  React.useEffect(() => {
    authSnapshotRef.current = {
      user,
      activeTenantId,
      tenants,
    };
  }, [activeTenantId, tenants, user]);

  const refresh = React.useCallback(async (): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await fetch("/api/me", {
        headers: { accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setUser(null);
          setActiveTenantId(null);
          setTenants([]);
          return false;
        }

        return Boolean(authSnapshotRef.current.user);
      }
      const payload = (await response.json()) as {
        user?: AuthUser;
        activeTenantId?: string | null;
        tenants?: TenantSummary[];
      };
      setUser(payload.user ?? null);
      setActiveTenantId(payload.activeTenantId ?? null);
      setTenants(Array.isArray(payload.tenants) ? payload.tenants : []);
      if (payload.activeTenantId) {
        localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, payload.activeTenantId);
      } else {
        localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
      }
      return Boolean(payload.user);
    } catch (error) {
      console.error("Failed to load session", error);
      return Boolean(authSnapshotRef.current.user);
    } finally {
      setLoading(false);
    }
  }, []);

  const setActiveTenant = React.useCallback(async (tenantId: string) => {
    if (!tenantId) return;
    const response = await fetch("/api/active-tenant", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ tenantId }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error ?? `Failed to set tenant (status ${response.status})`);
    }
    const payload = (await response.json()) as { activeTenantId?: string };
    const nextTenantId = payload.activeTenantId ?? tenantId;
    setActiveTenantId(nextTenantId);
    localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, nextTenantId);
    await refresh();
  }, [refresh]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  React.useEffect(() => {
    if (!user || loading) return;
    if (activeTenantId === null && tenants.length === 1) {
      void setActiveTenant(tenants[0].id);
    }
  }, [activeTenantId, loading, setActiveTenant, tenants, user]);

  React.useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (!user) return;
      if (event.key !== ACTIVE_TENANT_STORAGE_KEY) return;
      if (!event.newValue || event.newValue === activeTenantId) return;
      void setActiveTenant(event.newValue);
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [activeTenantId, setActiveTenant, user]);

  React.useEffect(() => {
    if (user) return;
    setActiveTenantId(null);
    setTenants([]);
    localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
  }, [user]);

  React.useEffect(() => {
    const handleTenantRequired = () => {
      void refresh();
    };
    window.addEventListener("pericles-tenant-required", handleTenantRequired);
    return () => window.removeEventListener("pericles-tenant-required", handleTenantRequired);
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, activeTenantId, tenants, loading, refresh, setActiveTenant, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
